import uuid from 'node-uuid';
import { RSAA } from 'redux-api-middleware';
import { uniq, uniqBy, get, has, flatten, pickBy, partition } from 'lodash';
import endpoints from './endpoints';
import { getAccountById, getAccountBySortCodeAccountNumber } from '../states/accounts/selectors';
import { getBeneficiaryByIdForTemplates } from '../states/commonSelectors/selectors';
import { updateAccounts } from '../states/accounts/actions';
import { setPayees } from '../states/payees/actions';
import { getPayeeBySortCodeAccountNumber } from '../states/payees/selectors';
import { getIsLCV } from '../states/modes/selectors';
import { getBankId } from '../states/session/selectors';
import { resolveEndpoint } from './endpointUtils';
import { isSuccessCode } from '../helpers/apiResponse';

export const GET_ALL_MISSING_DETAILS_REQUEST = 'GET_ALL_MISSING_DETAILS_REQUEST';
export const GET_ALL_MISSING_DETAILS_SUCCESS = 'GET_ALL_MISSING_DETAILS_SUCCESS';
export const GET_ALL_MISSING_DETAILS_FAILURE = 'GET_ALL_MISSING_DETAILS_FAILURE';

export const getMultiPartRequest = (endpoint, urlParams, bankId) => {
	const uri = endpoint.resolve(urlParams);
	const { method, x_bpi_version } = endpoint;
	return {
		id: uuid.v1(),
		method,
		uri: resolveEndpoint(uri, { bankId }),
		x_bpi_version,
	};
};

export const handleAccountsAndBensMultiPartSuccess = (response) => (dispatch) => {
	if (response.type === GET_ALL_MISSING_DETAILS_SUCCESS) {
		const apiResponses = get(response, 'payload.apiResponses', []);
		if (apiResponses.some((apiResponse) => !isSuccessCode(apiResponse.code))) {
			return dispatch({
				type: GET_ALL_MISSING_DETAILS_FAILURE,
			});
		}

		const accounts = apiResponses
			.filter((response) => has(response, 'body.accounts'))
			.flatMap((response) => get(response, 'body.accounts'));
		const beneficiaries = apiResponses
			.filter((response) => has(response, 'body.beneficiaries'))
			.flatMap((response) => get(response, 'body.beneficiaries'));

		if (accounts && accounts.length) {
			dispatch(updateAccounts(accounts));
		}
		if (beneficiaries && beneficiaries.length) {
			dispatch(setPayees(beneficiaries));
		}
	}
	return null;
};

export const editFPAccountsAndBensMultiPartSuccess = (response) => (dispatch) => {
	if (response.type === GET_ALL_MISSING_DETAILS_SUCCESS) {
		const apiResponses = get(response, 'payload.apiResponses', []);

		if (apiResponses.some((apiResponse) => !isSuccessCode(apiResponse.code))) {
			return dispatch({
				type: GET_ALL_MISSING_DETAILS_FAILURE,
			});
		}

		const responseBodies = apiResponses.map((response) => response.body);
		const [accounts = [], beneficiaries = []] = partition(responseBodies, (response) =>
			has(response, 'actions_available')
		);

		if (accounts.length) {
			dispatch(updateAccounts(accounts));
		}
		if (beneficiaries.length) {
			dispatch(setPayees(beneficiaries));
		}
	}
	return null;
};

export const getAccountsAndBensMultiPartRequest = (requestBody) => {
	if (!requestBody.length) {
		return null;
	}
	const endpoint = endpoints.multipart.uri;
	const body = JSON.stringify({
		batch_list: requestBody,
	});
	return {
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [GET_ALL_MISSING_DETAILS_REQUEST, GET_ALL_MISSING_DETAILS_SUCCESS, GET_ALL_MISSING_DETAILS_FAILURE],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	};
};

export const getMissingAccountAndBeneficiaries =
	({ accounts = [], beneficiaries = [] }, bankId) =>
	(dispatch) => {
		const accountsList = accounts.map((accountId) =>
			getMultiPartRequest(endpoints.getAccountDetails, accountId, bankId)
		);
		const beneficiariesList = beneficiaries.map((beneficiaryId) =>
			getMultiPartRequest(endpoints.getBeneficiaryById, beneficiaryId, bankId)
		);

		return dispatch(getAccountsAndBensMultiPartRequest([...accountsList, ...beneficiariesList])).then((response) => {
			dispatch(editFPAccountsAndBensMultiPartSuccess(response));
			return response;
		});
	};

export const getMissingAccountsAndBensForEditPayments = ({ payments = [], state }) => {
	const bankId = getBankId(state);
	const missingDetails = payments.reduce(
		(missingInfo, payment) => {
			const { fromAccount, toAccount } = payment;
			if (!getAccountById(state, fromAccount)) {
				missingInfo.accounts.push(fromAccount);
			}
			if (payment.isInternalPayment && !getAccountById(state, toAccount)) {
				missingInfo.accounts.push(toAccount);
				return missingInfo;
			}
			if (!getBeneficiaryByIdForTemplates(state, toAccount)) {
				missingInfo.beneficiaries.push(toAccount);
			}
			return missingInfo;
		},
		{ accounts: [], beneficiaries: [] }
	);

	missingDetails.accounts = uniq(missingDetails.accounts);
	missingDetails.beneficiaries = uniq(missingDetails.beneficiaries);
	const { accounts, beneficiaries } = missingDetails;
	if (accounts.length || beneficiaries.length) {
		return getMissingAccountAndBeneficiaries(missingDetails, bankId);
	}
	return null;
};

export const checkIfFromAccountAvailableInStore = ({ getState, from_account = {} }) => {
	const state = getState();
	const bankId = getBankId(state);
	const fromId = get(from_account, 'id', '') || get(from_account, 'account_uuid', '');
	const { sort_code: sortCode = '', account_number: accountNumber = '' } = from_account;
	if (fromId && !getAccountById(state, fromId)) {
		return getMultiPartRequest(endpoints.getAccountDetails, fromId, bankId);
	}
	if (from_account && !getAccountBySortCodeAccountNumber(state, sortCode, accountNumber)) {
		return getMultiPartRequest(endpoints.getLcvAccounts, { accountNumber, sortCode }, bankId);
	}
	return null;
};

export const checkIfToSelectionAvailableInStore = ({ getState, to_account = {} }) => {
	const state = getState();
	const bankId = getBankId(state);
	const toId = get(to_account, 'id', '') || get(to_account, 'account_uuid', '');
	const { sort_code: sortCode = '', account_number: accountNumber = '', iban = '', name = '' } = to_account;
	if (toId && !getBeneficiaryByIdForTemplates(state, toId)) {
		return getMultiPartRequest(endpoints.getBeneficiaryById, toId, bankId);
	}
	if (to_account && !getPayeeBySortCodeAccountNumber(state, sortCode, accountNumber || iban)) {
		const toSelectionDetails = pickBy({
			accountNumber: accountNumber || iban,
			sortCode,
			name,
		});
		return getMultiPartRequest(endpoints.getLcvPayees, toSelectionDetails, bankId);
	}
	return null;
};

export const getMultipartRequestBodyForPayments = ({ from_account, to_account }, getState) => {
	const requestBody = [];
	requestBody.push(checkIfFromAccountAvailableInStore({ getState, from_account }));
	requestBody.push(checkIfToSelectionAvailableInStore({ getState, to_account }));
	return requestBody.filter((request) => request);
};

export const getMissingDetailsForChapsAndIFTEditPayment = (payment) => (dispatch, getState) => {
	const state = getState();
	const isLCV = getIsLCV(state);
	if (!isLCV) {
		return Promise.resolve();
	}
	const requestBody = getMultipartRequestBodyForPayments(payment, getState);
	return dispatch(getAccountsAndBensMultiPartRequest(requestBody)).then((response) => {
		dispatch(handleAccountsAndBensMultiPartSuccess(response));
		return response;
	});
};

export const getUniqAndFlattenArray = (list) => uniqBy(flatten(list), 'uri');

export const getMissingAccountsForPaymentLibAuth = (paymentList) => (dispatch, getState) => {
	const requestBody = getUniqAndFlattenArray(
		paymentList.map((payment) => getMultipartRequestBodyForPayments(payment, getState))
	);
	return dispatch(getAccountsAndBensMultiPartRequest(requestBody)).then((response) => {
		dispatch(handleAccountsAndBensMultiPartSuccess(response));
		return response;
	});
};
